import { CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons';
import { ReactComponent as MasterOutlined } from './master-template-icon.svg';
import WidgetModel from '@mm/state/model/WidgetModel';
import classNames from 'classnames';
import React, { ReactNode, useState } from 'react';
import { Menu } from '../../../../../features/beta/modules/Automations/AutomationEdit/DialogueEditor/NodeTypes/elements/NodeMenu/NodeMenu';
import { EmailForm } from '../EmailForm/EmailForm';
import { ReactComponent as ExportOutlined } from '../../../../../features/beta/modules/Dashboard/AutomationsDashboard/open-link-icon.svg';
import styles from './EmailTemplatesTable.module.css';
import { Switch } from 'antd';
import { Button } from '@mm/components';
import { useEmailWidgetHandlers } from '../../../../../../common/helpers/widget-helpers/use-email-widget-handlers';
import { cloneDeep, set } from 'lodash';

const EmailTemplateRow = ({
  name,
  onClick,
  onDelete,
  isExpanded,
  isMaster,
  statusComponent,
  css,
}: {
  name: string | ReactNode;
  onClick: () => void;
  onDelete: (url: string) => void;
  isExpanded: boolean;
  isMaster: boolean;
  statusComponent: ReactNode;
  css?: {
    leftRow?: string;
  };
}) => {
  const menu = [
    {
      disabled: false,
      children: 'Delete',
      onClick: () => onDelete(name?.toString() || ''),
    },
  ];
  return (
    <div
      className={classNames(styles.row, { [styles.rowExpanded]: isExpanded })}
    >
      <div
        className={classNames(styles.rowLeft, css?.leftRow, {
          [styles.rowLeftExpanded]: isExpanded,
        })}
        onClick={onClick}
      >
        {isExpanded ? (
          <CaretDownOutlined className={styles.caret} />
        ) : (
          <CaretRightOutlined className={styles.caret} />
        )}
        {name}
        {isExpanded && !isMaster && (
          <ExportOutlined
            className={styles.openLinkIcon}
            onClick={() => typeof name === 'string' && window.open(name)}
          />
        )}
      </div>

      {statusComponent}
      {!isMaster && (
        <Menu
          className={styles.menu}
          menu={menu}
          direction="bottom"
          useThreeDotButton
          css={{ menu: styles.nodeMenu }}
        />
      )}
    </div>
  );
};

export const EmailTemplatesTable = ({
  selectedLandings,
  onChangeSelectedLandings,
  widget,
  onUpgradeClick,
  stateAndHandlers,
  onSignatureClick,
}: {
  stateAndHandlers: ReturnType<typeof useEmailWidgetHandlers>;
  selectedLandings: string[];
  onChangeSelectedLandings: (url: string) => void;
  widget: WidgetModel;
  onUpgradeClick: () => void;
  onSignatureClick?: () => void;
}) => {
  const { configEntities, setConfigEntities } = stateAndHandlers;
  const masterHasBody = !!configEntities.find(
    entity => entity.master && entity.body,
  );
  const [expandedTemplate, setExpandedTemplate] = useState(
    masterHasBody ? '' : 'master',
  );
  const [touched, setTouched] = useState<Set<string>>(new Set());

  const onToggleExpandedTemplate = url => {
    if (expandedTemplate === url) {
      setExpandedTemplate('');
      setTouched(prev => prev.add(url));
    } else {
      setExpandedTemplate(url);
    }
  };

  const onGenerateButtonClick = url => {
    onToggleExpandedTemplate(url);
  };

  const statusComponent = url => {
    const currentEntity = configEntities.find(ent => ent.url === url);
    const currentEntityIndex = configEntities.findIndex(ent => ent.url === url);

    const updateConfig = value => {
      const updatedConfig = cloneDeep(configEntities);
      if (currentEntity) {
        set(updatedConfig[currentEntityIndex], 'status', value);
      }
      setConfigEntities(updatedConfig);
    };

    if (!currentEntity?.body && expandedTemplate !== currentEntity?.url) {
      return (
        <Button
          css={{ container: styles.button }}
          onClick={() => onGenerateButtonClick(url)}
        >
          Generate
        </Button>
      );
    }

    return (
      <Switch
        checkedChildren="active"
        unCheckedChildren="pause"
        className={styles.switch}
        checked={currentEntity?.status === 'active'}
        onClick={active => updateConfig(active ? 'active' : 'disabled')}
      />
    );
  };

  return (
    <div className={styles.table}>
      <div className={styles.tableHeader}>
        <div>
          Customize emails{' '}
          <span className={styles.note} style={{ fontWeight: 500 }}>
            {selectedLandings.length}/20
          </span>
        </div>
        <div>Status</div>
        <div className={styles.actionHeader}>Action</div>
      </div>

      {selectedLandings.map(url => {
        const isCurrentTemplateExpanded = url === expandedTemplate;

        const currentEntity = configEntities?.find(
          entity => entity.url === url,
        );
        const hasEntityBody = currentEntity?.body;

        const isMasterTemplate = !!currentEntity?.master;
        const statusMasterComponent = url =>
          !hasEntityBody ? statusComponent(url) : 'Default';

        const showError =
          !hasEntityBody && touched.has(url) && !expandedTemplate.includes(url);

        const name = isMasterTemplate ? (
          <div className={styles.masterRow}>
            <MasterOutlined />
            Master Email Template{' '}
            <span
              className={classNames(styles.note, {
                [styles.erroredRow]: showError,
              })}
            >
              Your default email
            </span>
          </div>
        ) : (
          url
        );

        return (
          <div
            key={url}
            className={classNames(styles.rowItemWrapper, {
              [styles.activeTemplate]: isCurrentTemplateExpanded,
            })}
          >
            <EmailTemplateRow
              name={name}
              onClick={() => onToggleExpandedTemplate(url)}
              isExpanded={isCurrentTemplateExpanded}
              isMaster={isMasterTemplate}
              statusComponent={
                isMasterTemplate
                  ? statusMasterComponent(url)
                  : statusComponent(url)
              }
              onDelete={onChangeSelectedLandings}
              css={{
                leftRow: classNames({
                  [styles.inactiveRow]: !hasEntityBody,
                  [styles.erroredRow]: showError,
                }),
              }}
            />
            {isCurrentTemplateExpanded && (
              <EmailForm
                widget={widget}
                entityUrl={currentEntity?.url}
                stateAndHandlers={stateAndHandlers}
                onUpgradeClick={onUpgradeClick}
                styles={{
                  container: styles.emailForm,
                  optionalBlock: styles.ccBlock,
                }}
                onSignatureClick={onSignatureClick}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
